import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { useOrganizationMaintenanceQuery } from "../../../api/network/maintenanceApiService";
import {
  setListingQueryParams,
  TListingQueryParams,
  TModalsState,
} from "../../../api/store/commonSlice";
import { FilterType } from "../../../api/types/Admin";
import { TLegacyMaintenance } from "../../../api/types/Maintenance";
import { TSelectboxOption } from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import AdminTable from "../../../components/adminTable";
import ListingPageSubHeader from "../../../components/listingPageSubHeader";
import ListingTableHeader from "../../../components/listingTableHeader";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../../components/modalEditListingColumns";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useListingFilters } from "../../../hooks/listingFilter";
import { routeUrls } from "../../../navigation/routeUrls";
import { SortingStatusWithReset } from "../../../utils/adminListings";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { useLoggedInUserData } from "../../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import { TAllColumn } from "../vehicles/vehicles";

const columnOrderingFields: Record<string, string> = {
  "Asset ID": "deviceid",
  "Asset Description": "description",
  "Display Name": "displayname",
  "Driver ID": "driverid",
  Driver: "drivername",
  "Unique ID": "uniqueid",
  "Equipment Type (VIN)": "equipmenttype",
  "Server ID": "devicecode",
  "Ignition State": "ignition_state",
  "Camera ID": "cameraid",
  Active: "isactive",
};

const ScreenDashboardAdminMaintenance = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.maintenance",
  });
  const { t: tMain } = useTranslation();
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { maintenance: orgMaintenanceQueryParams } = listingQueryParams;
  const dispatch = useDispatch();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);
  const { getUserACLAccessLevel } = useACL();
  // video allowed at account level
  const thisOrgViewVideoAllowed = useLoggedInUserData("viewVideoAllowed");
  const [all_columns, setAllColumns] = React.useState<TAllColumn[]>([]);
  useEffect(() => {
    setAllColumns([
      { name: "Asset ID", acl: "" },
      { name: "Asset Description", acl: "" },
      { name: "Display Name", acl: "" },
      { name: "Driver ID", acl: "" },
      ...(showDriver ? [{ name: "Driver", acl: "" }] : []),
      { name: "Unique ID", acl: ACL_NAMES.ACL_ASSET_UNIQUE_ID },
      { name: "Equipment Type (VIN)", acl: "" },
      { name: "Server ID", acl: "" }, // , acl: ACL_NAMES.ACL_ASSET_SERVER_ID
      { name: "Ignition State", acl: "" },
      {
        name: "Camera ID",
        acl: ACL_NAMES.ACL_ASSET_EDIT_CAMERA,
        accountLevelAccess: thisOrgViewVideoAllowed === "1",
      },
      { name: "Active", acl: ACL_NAMES.ACL_ASSET_ACTIVE },
    ]);
  }, [thisOrgViewVideoAllowed]);
  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(
    localStorageKeys.columns.Maintenance
  );
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns
      .filter((column) => {
        return (
          (column.acl
            ? getUserACLAccessLevel(column.acl) > ROLE_ACCESS_LEVELS.NONE
            : true) &&
          (column?.accountLevelAccess !== undefined
            ? column?.accountLevelAccess
            : true)
        );
      })
      .map((colItem, _) => {
        return {
          name: colItem.name,
          show: !!savedColumns ? savedColumns.includes(colItem.name) : true,
        };
      })
  );
  useEffect(() => {
    setColumns(
      // all_columns
      //   .filter((column) =>
      //     column.acl
      //       ? getUserACLAccessLevel(column.acl) > ROLE_ACCESS_LEVELS.NONE
      //       : true
      //   )
      //   .map((colItem, _) => {
      //     return {
      //       name: colItem.name,
      //       show: !!savedColumns ? savedColumns.includes(colItem.name) : true,
      //     };
      //   })
      all_columns
        .filter((column) => {
          return (
            (column.acl
              ? getUserACLAccessLevel(column.acl) > ROLE_ACCESS_LEVELS.NONE
              : true) &&
            (column?.accountLevelAccess !== undefined
              ? column?.accountLevelAccess
              : true)
          );
        })
        .map((colItem, _) => {
          return {
            name: colItem.name,
            show: !!savedColumns ? savedColumns.includes(colItem.name) : true,
          };
        })
    );
  }, [savedColumns, all_columns]);

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        maintenance: { ...orgMaintenanceQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  const handleOrderingChange = (ordering?: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        maintenance: {
          ...orgMaintenanceQueryParams,
          ordering,
          page: 1,
        },
      })
    );
  };

  // filter mechainism
  const { filters } = useListingFilters();
  const [activeFilterSlug, setActiveFilterSlug] = React.useState<FilterType>(
    filters?.[0]?.slug
  );

  // updating query params on filter change
  useEffect(() => {
    const newParams = {
      ...listingQueryParams,
      maintenance: {
        ...orgMaintenanceQueryParams,
        isactive: undefined,
        page: 1,
        ...(activeFilterSlug === "both"
          ? {}
          : {
              isactive: activeFilterSlug === "active" ? 1 : 0,
            }),
      },
    };
    dispatch(setListingQueryParams(newParams));
  }, [activeFilterSlug]);

  const {
    data: dataOrgMaintenance,
    isFetching: isFetchingOrgMaintenance,
    error,
  } = useOrganizationMaintenanceQuery({
    ...orgMaintenanceQueryParams,
    // 'search_fields' intentionally not added here as default search fields are defined in the API service
  });
  const { count, next, previous, results } = dataOrgMaintenance || {};

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataOrgMaintenance]);

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as TLegacyMaintenance[])).map(
        (item: TLegacyMaintenance, index: number) => ({
          navLink: `${routeUrls.dashboardChildren.maintenance}/${item?.deviceid}`,
          cellData: [
            item?.deviceid, // item?.vehicleid, // "Asset Id",
            item?.description, // "Asset Description",
            item?.displayname, // Display Name
            item?.driverid ?? "-", // "Driver Id",
            ...(showDriver ? [item?.drivername ?? "-"] : []),
            item?.uniqueid, // "Unique Id",

            item?.equipmenttype ?? "-", // "Equipment Type (VIN)",
            // item?.simphonenumber ?? "-", // "SIM Phone",
            item?.devicecode ?? "-", // "Ignition State",
            item?.ignition_state ?? "-", // "Ignition State",
            item?.cameraid ?? "-", // "Recorder Id",
            item?.isactive === 1 ? (
              <span className="text-field-success">{tMain("yes")}</span>
            ) : item?.isactive === 0 ? (
              <span className="text-field-error-dark">{tMain("no")}</span>
            ) : (
              "-"
            ), // "Active"
          ].filter((_, index) => columns[index]?.show),
          isActive: item?.isactive === 1,
        })
      )
    : [];

  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader
          heading={t("sub_heading")}
          // buttonText={t("add_new")}
          // buttonCallback={() => {
          //   // dispatch(setModalsData({ ...modalsState, showCreateVehicle: true }))
          // }}
        />
        <ListingTableHeader
          heading={
            <div className="flex items-center gap-4">
              {/* Heading */}
              <span>{t("listing_heading")}</span>

              {/* Sorted by label */}
              {!!orgMaintenanceQueryParams?.ordering && (
                <SortingStatusWithReset
                  ordering={orgMaintenanceQueryParams.ordering}
                  columnOrderingFields={columnOrderingFields}
                  onResetOrdering={() => handleOrderingChange(undefined)}
                />
              )}
            </div>
          }
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={orgMaintenanceQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
          filters={filters}
          activeFilterSlug={activeFilterSlug}
          handleFilterChange={(slug) => setActiveFilterSlug(slug)}
        />
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.Maintenance}
        />
        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton disabled={isFetchingOrgMaintenance} />
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetchingOrgMaintenance}
            listingQueryParams={orgMaintenanceQueryParams}
            listingWrapperRef={listingWrapperRef}
            onOrderingChange={handleOrderingChange}
            ordering={orgMaintenanceQueryParams.ordering}
            sortableColumns={columnOrderingFields}
          />
          {!isFetchingOrgMaintenance && (
            <Pagination
              pageSize={orgMaintenanceQueryParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    maintenance: {
                      ...orgMaintenanceQueryParams,
                      page: 1,
                      page_size: parseInt(`${e?.value}`),
                    },
                  })
                );
              }}
              totalPages={
                count
                  ? Math.ceil(count / orgMaintenanceQueryParams.page_size)
                  : 1
              }
              forcePage={orgMaintenanceQueryParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    maintenance: {
                      ...orgMaintenanceQueryParams,
                      page: data?.selected + 1,
                    },
                  })
                );
              }}
              paginCurrentPage={orgMaintenanceQueryParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ScreenDashboardAdminMaintenance;
